import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Panel } from 'primereact/panel';
import { TabView, TabPanel } from 'primereact/tabview';
import { Message } from 'primereact/message';
import { allocationsApi, AllocationSuggestion } from '../api';
import AllocationSuggestionsTab from '../components/reconciliation/AllocationSuggestionsTab';
import { ManualDepositMatchTab } from '../components/reconciliation/ManualDepositMatchTab';
import { ManualPurchaseMatchTab } from '../components/reconciliation/ManualPurchaseMatchTab';
import { CustomerBalancesTab } from '../components/reconciliation/CustomerBalancesTab';
import { QBSyncTab } from '../components/reconciliation/QBSyncTab';

export default function Reconciliation() {
  const { clientId } = useParams<{ clientId: string }>();
  const [suggestions, setSuggestions] = useState<AllocationSuggestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingLedgerEntryId, setSavingLedgerEntryId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const loadData = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      // Load any data needed
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAcceptSuggestion = async (suggestion: AllocationSuggestion) => {
    if (!clientId) return;
    setSaving(true);
    setSavingLedgerEntryId(suggestion.ledger_entry_id);
    try {
      await allocationsApi.acceptAllocationSuggestions(clientId, [suggestion]);
      // Refresh suggestions and allocations
      setSuggestions(suggestions.filter(s => s.ledger_entry_id !== suggestion.ledger_entry_id));
      await loadData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to accept suggestion';
      setError(message);
      throw err instanceof Error ? err : new Error(message);
    } finally {
      setSaving(false);
      setSavingLedgerEntryId(null);
    }
  };

  if (!clientId) {
    return (
      <>
        <Message severity="error" text="Client ID is required" />
      </>
    );
  }

  return (
    <Panel>
      {error && <Message severity="error" text={error} />}

      <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
        <TabPanel header="Allocation Suggestions">
          <AllocationSuggestionsTab
            clientId={clientId}
            suggestions={suggestions}
            onAccept={handleAcceptSuggestion}
            onSuggestionsChange={setSuggestions}
            onRefresh={loadData}
            onError={setError}
            saving={saving}
            savingLedgerEntryId={savingLedgerEntryId}
          />
        </TabPanel>

        <TabPanel header="Match Deposits">
          <ManualDepositMatchTab clientId={clientId} />
        </TabPanel>

        <TabPanel header="Match Purchases">
          <ManualPurchaseMatchTab clientId={clientId} />
        </TabPanel>

        <TabPanel header="Customer Balances">
          <CustomerBalancesTab clientId={clientId} />
        </TabPanel>

        <TabPanel header="QB Sync">
          <QBSyncTab clientId={clientId} />
        </TabPanel>
      </TabView>
    </Panel>
  );
}

export { Reconciliation };
